import apiClient from './apiClient';
import Note from '../models/Note';

class NoteService {
  async getNotes(params = {}) {
    try {
      const response = await apiClient.get('/notes/all', params);
      // Check if response itself is the array or has a notes property
      const notesArray = Array.isArray(response) ? response : response.notes;
      if (!Array.isArray(notesArray)) {
          console.error("Invalid notes response structure:", response);
          throw new Error("Notes data is not an array");
      }
      console.log("Notes array:", notesArray);
      return {
        notes: notesArray.map(Note.fromJson), // Map the correct array
        // Use pagination from response if available, otherwise calculate based on notesArray
        pagination: response.pagination || { 
          total: notesArray.length,
          page: 1,
          pageSize: notesArray.length,
        }
      };
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  }

  async getNoteById(noteId) {
    try {
      const response = await apiClient.get(`/notes/${noteId}`);
      return Note.fromJson(response);
    } catch (error) {
      console.error(`Error fetching note with ID ${noteId}:`, error);
      throw error;
    }
  }

  async getPublicNote(noteId) {
    try {
      // skipAuth=true to bypass authentication for public notes
      const response = await apiClient.get(`/notes/${noteId}`, {}, true);
      const note = Note.fromJson(response);
      if (!note.isShared) {
        throw new Error('This note is not public');
      }
      return note;
    } catch (error) {
      console.error(`Error fetching public note with ID ${noteId}:`, error);
      throw error;
    }
  }

  async getSharedNote(shareToken) {
    try {
      const response = await apiClient.get(`/notes/shared/${shareToken}`, {}, true);
      return Note.fromJson(response);
    } catch (error) {
      console.error(`Error fetching shared note with token ${shareToken}:`, error);
      throw error;
    }
  }

  async createNote(noteData) {
    try {
      const response = await apiClient.post('/notes/new', noteData);
      return Note.fromJson(response);
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  }

  async updateNote(noteId, noteData) {
    try {
      const response = await apiClient.put(`/notes/${noteId}`, noteData);
      return Note.fromJson(response);
    } catch (error) {
      console.error(`Error updating note with ID ${noteId}:`, error);
      throw error;
    }
  }

  async deleteNote(noteId) {
    try {
      await apiClient.delete(`/notes/${noteId}`);
      return true;
    } catch (error) {
      console.error(`Error deleting note with ID ${noteId}:`, error);
      throw error;
    }
  }

  async shareNote(noteId) {
    try {
      const response = await apiClient.post(`/notes/${noteId}/share`);
      return Note.fromJson(response);
    } catch (error) {
      console.error(`Error sharing note with ID ${noteId}:`, error);
      throw error;
    }
  }

  async unshareNote(noteId) {
    try {
      const response = await apiClient.delete(`/notes/${noteId}/share`);
      return Note.fromJson(response);
    } catch (error) {
      console.error(`Error unsharing note with ID ${noteId}:`, error);
      throw error;
    }
  }

  async togglePublic(noteId, currentState) {
    try {
      const response = await apiClient.put(`/notes/${noteId}/toggle-public`, { is_public: !currentState });
      return Note.fromJson(response);
    } catch (error) {
      console.error(`Error toggling public status for note with ID ${noteId}:`, error);
      throw error;
    }
  }

  // Method to create a note when not logged in (guest mode)
  async createGuestNote(noteData) {
    try {
      const response = await apiClient.post('/notes/new', noteData, true);
      return Note.fromJson(response);
    } catch (error) {
      console.error('Error creating guest note:', error);
      throw error;
    }
  }

  async getCategories() {
    try {
      // Assuming an endpoint like /notes/categories or just /categories exists
      const response = await apiClient.get('/tasks/categories'); 
      // Assuming the response is directly the array of categories
      return Array.isArray(response) ? response : response.categories || [];
    } catch (error) {
      console.error('Error fetching note categories:', error);
      throw error;
    }
  }

  async getThreadsByCategoryId(categoryId) {
    try {
      const response = await apiClient.get(`/categories/threads/category/${categoryId}`);
      return response.threads;
    } catch (error) {
      console.error('Error fetching threads:', error);
      throw error;
    }
  }

  async upsertThread(thread) {
    try {
      const response = await apiClient.post('/categories/threads/upsert', thread);
      return response.thread;
    } catch (error) {
      console.error('Error upserting thread:', error);
      throw error;
    }
  }

  async generateGanttChart(categoryId) {
    try {
      const response = await apiClient.get(`/categories/threads/gantt/${categoryId}`);
      return response.mermaid_code;
    } catch (error) {
      console.error('Error generating gantt chart:', error);
      throw error;
    }
  }
}
const noteServiceInstance = new NoteService();
export default noteServiceInstance; 